import { STAGE_SLA_MS, type LeadStage } from "./leadQuality";
import { windowState } from "./window";
import { isWindowGated, type Channel } from "./types";

/**
 * X4 TEAM (Composer Spec team routing, Planning elements, CLAUDE.md sections 3, 7
 * and 8). Which person a new conversation is handed to, and how late a person is
 * running on one. Pure: no clock, no IO, no model. Every input is passed in.
 *
 * VIP -> OWNER IS A CODE CONSTANT, not configuration (section 3: VIP status
 * changes routing and service level, never a number). `routing_config` cannot
 * turn it off, and no config field names it.
 *
 * THE OWNER IS NOT IN THE ROTATION. The owner receives VIP threads and whatever
 * a human hands them; a normal new thread goes to on-shift staff or stays
 * unassigned (the AI holds it solo and the board shows it). Putting the owner in
 * the rotation would make "unassigned" disappear on any shop with no staff on
 * shift, which is the one state the board exists to show.
 *
 * NOTHING HERE SENDS OR DRAFTS. An assignment changes who is asked to look at a
 * thread, never what is said or whether it may be said (the window machine, the
 * arbiter and the gate chain are untouched).
 */

export const TEAM_ROUTING_MODES = ["round_robin", "expertise"] as const;
export type TeamRoutingMode = (typeof TEAM_ROUTING_MODES)[number];

/** Why a thread went where it went (the `thread.assigned` reason codes). */
export const ASSIGNMENT_REASONS = [
  "vip_owner",
  "expertise",
  "round_robin",
  "no_one_on_shift",
  "no_owner_for_vip",
  "manual",
  "taken",
  "handoff",
] as const;
export type AssignmentReason = (typeof ASSIGNMENT_REASONS)[number];

export const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export interface ShiftBlock {
  /** "HH:MM", 24 hour, inclusive. */
  readonly from: string;
  /** "HH:MM" or "24:00", exclusive. Always after `from`: a shift never crosses midnight (write two blocks). */
  readonly to: string;
}
export type Schedule = Readonly<Partial<Record<Weekday, readonly ShiftBlock[]>>>;

export interface RoutingMember {
  readonly userId: string;
  readonly role: "owner" | "staff";
  readonly active: boolean;
  readonly expertise: readonly string[];
  readonly schedule: Schedule;
  /** IANA zone. The shift is read on the member's own wall clock. */
  readonly timezone: string;
}

export interface RoutingConfig {
  readonly mode: TeamRoutingMode;
  /** inquiry type -> the expertise tags that suit it. */
  readonly expertiseMap: Readonly<Record<string, readonly string[]>>;
}

const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$/;

const minutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":") as [string, string];
  return Number(h) * 60 + Number(m);
};

/**
 * A stored schedule, checked. Throws, naming the problem, on an unknown day, a
 * time that is not HH:MM, or a block that does not run forward. A schedule that
 * is quietly misread is a person who is never routed to, or always routed to.
 */
export function parseSchedule(raw: unknown): Schedule {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) throw new Error("schedule must be an object keyed by weekday");
  const out: Partial<Record<Weekday, ShiftBlock[]>> = {};
  for (const [day, blocks] of Object.entries(raw)) {
    if (!(WEEKDAYS as readonly string[]).includes(day)) throw new Error(`schedule: ${day} is not a weekday key (sun..sat)`);
    if (!Array.isArray(blocks)) throw new Error(`schedule.${day} must be a list of {from, to}`);
    const parsed: ShiftBlock[] = [];
    for (const b of blocks as unknown[]) {
      const block = b as { from?: unknown; to?: unknown } | null;
      if (typeof block?.from !== "string" || typeof block.to !== "string" || !HHMM.test(block.from) || !HHMM.test(block.to) || block.from === "24:00") {
        throw new Error(`schedule.${day}: each block needs from and to as HH:MM (to may be 24:00)`);
      }
      if (minutes(block.from) >= minutes(block.to)) throw new Error(`schedule.${day}: from must be before to (split a shift that crosses midnight)`);
      parsed.push({ from: block.from, to: block.to });
    }
    out[day as Weekday] = parsed;
  }
  return out;
}

/** The weekday and minute-of-day of `now` on a zone's wall clock. Throws on an unknown zone. */
function wallClock(now: Date, timezone: string): { day: Weekday; minute: number } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  } catch {
    throw new Error(`team routing: ${JSON.stringify(timezone)} is not a timezone this runtime knows`);
  }
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  const day = get("weekday").toLowerCase().slice(0, 3) as Weekday;
  return { day, minute: Number(get("hour")) * 60 + Number(get("minute")) };
}

export function isOnShift(member: RoutingMember, now: Date): boolean {
  if (!member.active) return false;
  const { day, minute } = wallClock(now, member.timezone);
  return (member.schedule[day] ?? []).some((b) => minute >= minutes(b.from) && minute < minutes(b.to));
}

export interface UncoveredWindow {
  readonly from: string;
  readonly to: string;
}

/**
 * The stretches inside `[from, from + hours)` in which no STAFF member is on shift (the owner never
 * covers: not in the rotation), sampled at one-hour steps from `from`. ISO instants. A window still
 * open at the end of the span closes at the span's end. For the coverage view's "after-hours cover".
 */
export function uncoveredWindows(members: readonly RoutingMember[], from: Date, hours: number): UncoveredWindow[] {
  const staff = members.filter((m) => m.role === "staff");
  const HOUR = 3_600_000;
  const out: UncoveredWindow[] = [];
  let open: number | null = null;
  for (let i = 0; i < hours; i++) {
    const t = from.getTime() + i * HOUR;
    const covered = staff.some((m) => isOnShift(m, new Date(t)));
    if (!covered && open === null) open = t;
    if (covered && open !== null) {
      out.push({ from: new Date(open).toISOString(), to: new Date(t).toISOString() });
      open = null;
    }
  }
  if (open !== null) out.push({ from: new Date(open).toISOString(), to: new Date(from.getTime() + hours * HOUR).toISOString() });
  return out;
}

export interface RouteInput {
  readonly inquiryType: string | null;
  readonly vip: boolean;
  readonly members: readonly RoutingMember[];
  readonly config: RoutingConfig;
  /** `routing_config.rr_cursor`. */
  readonly cursor: number;
  readonly now: Date;
}

export interface RouteResult {
  readonly assigneeUserId: string | null;
  readonly reason: AssignmentReason;
  /** The cursor to store. Moves only when a rotation turn was taken. */
  readonly nextCursor: number;
}

const byUserId = (a: RoutingMember, b: RoutingMember): number => (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0);

export function routeThread(input: RouteInput): RouteResult {
  if (input.vip) {
    const owner = [...input.members].filter((m) => m.role === "owner" && m.active).sort(byUserId)[0];
    return owner === undefined
      ? { assigneeUserId: null, reason: "no_owner_for_vip", nextCursor: input.cursor }
      : { assigneeUserId: owner.userId, reason: "vip_owner", nextCursor: input.cursor };
  }

  const onShift = input.members.filter((m) => m.role === "staff" && isOnShift(m, input.now)).sort(byUserId);
  if (onShift.length === 0) return { assigneeUserId: null, reason: "no_one_on_shift", nextCursor: input.cursor };

  let pool = onShift;
  let reason: AssignmentReason = "round_robin";
  if (input.config.mode === "expertise" && input.inquiryType !== null) {
    const wanted = input.config.expertiseMap[input.inquiryType] ?? [];
    const experts = onShift.filter((m) => m.expertise.some((e) => wanted.includes(e)));
    if (experts.length > 0) {
      pool = experts;
      reason = "expertise";
    }
  }
  const pick = pool[((input.cursor % pool.length) + pool.length) % pool.length]!;
  return { assigneeUserId: pick.userId, reason, nextCursor: input.cursor + 1 };
}

export type SlaChip = "ok" | "warn" | "risk" | "grace" | "none";

export interface SlaChipInput {
  readonly stage: LeadStage | null;
  readonly lastCustomerMessageAt: Date | null;
  readonly lastOutboundAt: Date | null;
  readonly channel: Channel;
  readonly now: Date;
}

export interface SlaChipResult {
  readonly chip: SlaChip;
  /** Whole minutes since the customer's message while the shop owes a reply, else null. */
  readonly waitingMinutes: number | null;
}

/**
 * The SLA chip on the board. The clock is `STAGE_SLA_MS` (T3, never an input
 * here) read from the customer's last message while the shop has not spoken
 * since. Amber ("warn") at half the SLA, red ("risk") at the SLA.
 *
 * The window gate beats the SLA (F-02, CLAUDE.md section 8): on a window-gated
 * channel whose window has closed nothing may be sent, so the chip reads none
 * rather than red; the board's window badge and the owner alert carry that case.
 * AWAITING RATIFICATION: amber at half the SLA is the design's reading; Kabir
 * sets the real fraction.
 */
export function slaChip(input: SlaChipInput): SlaChipResult {
  const { lastCustomerMessageAt, lastOutboundAt, now } = input;
  if (lastCustomerMessageAt === null) return { chip: "none", waitingMinutes: null };
  if (lastOutboundAt !== null && lastOutboundAt.getTime() >= lastCustomerMessageAt.getTime()) return { chip: "none", waitingMinutes: null };

  const elapsed = Math.max(0, now.getTime() - lastCustomerMessageAt.getTime());
  const waitingMinutes = Math.floor(elapsed / 60_000);
  if (input.stage === null || input.stage === "not_derivable") return { chip: "none", waitingMinutes };
  const window = isWindowGated(input.channel) ? windowState({ lastCustomerMessageAt, now }) : null;
  if (window === "closed") return { chip: "none", waitingMinutes };
  // F-01: the final 2 h before the window closes is its own state. On an engaged DM the 24 h SLA equals the window, so "risk" could
  // never show there; grace is what tells the board the door is about to shut. It grants nothing extra.
  if (window === "grace") return { chip: "grace", waitingMinutes };

  const sla = STAGE_SLA_MS[input.stage];
  if (sla === null) return { chip: "none", waitingMinutes };
  const chip: SlaChip = elapsed >= sla ? "risk" : elapsed >= sla / 2 ? "warn" : "ok";
  return { chip, waitingMinutes };
}
